<template>
  <ModalCard
    v-if="isOpen"
    :title="`Export ${getHeadingNameLabel(modelName)}`"
    :handle-close="() => $emit('update:isOpen', false)"
    width="60%"
  >
    <template #body-header>
      <div>
        Obtain a spreadsheet with a list of your items. You can tweak the
        exported list of attributes and annotations.
      </div>
    </template>
    <template #body>
      <div>
        <fieldset :disabled="isDownloading">
          <div
            class="is-flex is-align-items-center is-justify-content-space-between mb-4"
          >
            <div class="title is-size-6 mb-0">
              <span class="icon">
                <i class="fa-solid fa-list-ul" />
              </span>
              Items
              <span class="has-text-weight-light">(rows)</span>
            </div>
            <div class="is-flex is-align-items-center">
              <span
                v-tooltip="{
                  content: `When selecting <code>all</code>, the filters set
                on the data (columns filters, global search) apply. Only the
                filtered record will be exported`,
                  html: true,
                }"
                class="icon hoverable-icon has-text-info mr-2"
              >
                <i class="fa-solid fa-info-circle" />
              </span>
              <div class="buttons has-addons">
                <button
                  v-tooltip="{
                    content:
                      numSelected === 0
                        ? 'Select rows in table to use this option'
                        : undefined,
                  }"
                  class="button is-small"
                  style="width: 120px"
                  :disabled="numSelected < 1"
                  :class="{
                    'is-primary is-selected': exportSelected,
                  }"
                  @click="exportSelected = true"
                >
                  Selected ({{ numSelected }})
                </button>
                <button
                  class="button is-small"
                  :class="{
                    'is-primary is-selected': !exportSelected,
                  }"
                  style="width: 120px"
                  @click="exportSelected = false"
                >
                  All ({{ totalRecords }})
                </button>
              </div>
            </div>
          </div>
          <div
            class="is-flex is-align-items-center is-justify-content-space-between mb-4"
          >
            <div class="title is-size-6 mb-0">
              <span class="icon">
                <i class="fa-solid fa-columns" />
              </span>
              Attributes of items
              <span class="has-text-weight-light">(columns)</span>
            </div>
            <div
              class="is-flex is-align-items-center is-justify-content-space-between"
            >
              <div class="buttons has-addons">
                <button
                  style="width: 120px"
                  class="button is-small"
                  :disabled="visibleAttributeIds.length === 0"
                  :class="{
                    'is-primary is-selected':
                      exportOnlyVisibleAttributes === true,
                  }"
                  @click="exportOnlyVisibleAttributes = true"
                >
                  Visible ({{ visibleAttributeIds.length }})
                </button>
                <button
                  style="width: 120px"
                  class="button is-small"
                  :class="{
                    'is-primary is-selected':
                      exportOnlyVisibleAttributes === false,
                  }"
                  @click="exportOnlyVisibleAttributes = false"
                >
                  All ({{ totalAttributes }})
                </button>
              </div>
            </div>
          </div>
          <div
            class="is-flex is-align-items-center is-justify-content-space-between mb-4"
          >
            <div>
              <div class="title is-size-6" style="margin-bottom: 0.4rem">
                <span class="icon">
                  <i class="fa-solid fa-tags" />
                </span>
                Annotations of items
                <span class="has-text-weight-light">(columns)</span>
              </div>
            </div>
            <div class="is-flex is-align-items-center">
              <span
                v-tooltip="{
                  content: `<code>All with value</code> allows you to export all
                annotations columns where at least one of the record as a value`,
                  html: true,
                }"
                class="icon hoverable-icon has-text-info mr-2"
              >
                <i class="fa-solid fa-info-circle" />
              </span>
              <div class="buttons has-addons">
                <button
                  v-tooltip="
                    visibleAnnotationsIds.length === 0
                      ? 'No annotation is currently visible in table. Add some to use this option'
                      : undefined
                  "
                  style="width: 120px"
                  class="button is-small"
                  :disabled="visibleAnnotationsIds.length === 0"
                  :class="{
                    'is-primary is-selected': exportOnlyVisibleAnnotations,
                  }"
                  @click="exportOnlyVisibleAnnotations = true"
                >
                  Visible ({{ visibleAnnotationsIds.length }})
                </button>
                <button
                  class="button is-small"
                  style="width: 120px"
                  :class="{
                    'is-primary is-selected': !exportOnlyVisibleAnnotations,
                    'is-loading': relevantAnnotations === null,
                  }"
                  @click="exportOnlyVisibleAnnotations = false"
                >
                  All with value ({{ exportedAnnotationIds.length }})
                </button>
              </div>
            </div>
          </div>
        </fieldset>
      </div>
    </template>
    <template #footer>
      <div class="is-flex is-flex-direction-column">
        <p class="help mb-4">
          The generation of the spreadseet document may take several minutes
          when exporting many items
        </p>
        <BaseButton
          label="Download"
          size="small"
          variant="primary"
          :outlined="true"
          :icon="['fa-solid', 'fa-download']"
          :disabled="isDownloading || relevantAnnotations === null"
          :loading="isDownloading"
          style="margin-left: auto"
          @click="handleExport"
        />
      </div>
    </template>
  </ModalCard>
</template>

<script>
import { getHeadingNameLabel } from '@/utils'
import { downloadFile } from '@/utils/api'
export default {
  emits: ['update:isOpen'],
  props: {
    isOpen: Boolean,
    modelName: String,
    url: String,
    selectedRecordIds: {
      type: Array,
      default: () => [],
    },
    totalRecords: {
      type: Number,
      default: 0,
    },
    selectedRecordsAnnotationIds: {
      type: Array,
      default: () => [],
    },
    visibleAttributeIds: {
      type: Array,
      default: () => [],
    },
    totalAttributes: {
      type: Number,
      default: 0,
    },
    visibleAnnotationsIds: {
      type: Array,
      default: () => [],
    },
    relevantAnnotations: {
      type: Array,
      default: () => [],
    },
    queryFilters: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      exportSelected: false,
      exportOnlyVisibleAttributes: false,
      exportOnlyVisibleAnnotations: false,
      isDownloading: false,
    }
  },
  computed: {
    numSelected() {
      return this.selectedRecordIds.length
    },
    relevantAnnotationIds() {
      return (
        (this.relevantAnnotations &&
          this.relevantAnnotations.map((a) => a.value)) ||
        []
      )
    },
    exportedAnnotationIds() {
      let exportedAnnotIds = []
      if (this.exportOnlyVisibleAnnotations) {
        exportedAnnotIds = this.visibleAnnotationsIds
      } else {
        exportedAnnotIds = this.exportSelected
          ? this.selectedRecordsAnnotationIds
          : this.relevantAnnotationIds || []
      }
      return exportedAnnotIds
    },

    payloadAttributeField() {
      let visibleAttributeIds = []
      if (this.exportOnlyVisibleAttributes === true) {
        visibleAttributeIds = this.visibleAttributeIds
        const indexOfId = visibleAttributeIds.indexOf('id')
        if (indexOfId !== -1) visibleAttributeIds.splice(indexOfId, 1)
        visibleAttributeIds = ['id', ...this.visibleAttributeIds]
      }
      return visibleAttributeIds
    },

    payload() {
      let payload = {
        ...(this.exportSelected
          ? { ids: this.selectedRecordIds }
          : this.queryFilters),
      }

      return {
        ...payload,
        attribute_fields: this.payloadAttributeField,
        annotation_fields: this.exportedAnnotationIds,
      }
    },
  },
  watch: {
    isOpen(isOp) {
      if (isOp === true) {
        if (this.numSelected > 0) this.exportSelected = true
      } else if (isOp === false) {
        this.exportSelected = false
        this.exportOnlyVisibleAttributes = false
        this.exportOnlyVisibleAnnotations = false
      }
    },
  },
  methods: {
    getHeadingNameLabel,
    async handleExport() {
      this.isDownloading = true

      try {
        const response = await downloadFile({
          url: this.url,
          method: 'put',
          payload: this.payload,
        })
        if (response.status === 200) {
          this.$emit('update:isOpen', false)
        }
      } catch (e) {
        this.$store.commit('notify', {
          type: 'error',
          message:
            e.response.status === 405
              ? `[405 ${e.response.statusText}] You are currently trying to download data filtered with annotations, this is not implemented yet`
              : 'Server error.',
          isDismissable: false,
          duration: 6000,
        })
      } finally {
        this.isDownloading = false
      }
    },
  },
}
</script>
